/**
 * Pure formatters used by the pricing surface. Kept outside the
 * render components so the tests that pin label text can import them
 * cheaply.
 */
/** `$0` always renders as "Free" so the no-card tier stays clear
 *  regardless of the catalog's currency. */
export function formatPrice(price) {
    if (price.amount_minor === 0)
        return 'Free';
    const currency = price.currency.toUpperCase();
    const major = price.amount_minor / 100;
    try {
        const fmt = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            minimumFractionDigits: Number.isInteger(major) ? 0 : 2,
            maximumFractionDigits: 2,
        });
        return fmt.format(major);
    }
    catch {
        return `${major.toFixed(Number.isInteger(major) ? 0 : 2)} ${currency}`;
    }
}
/** Same price without the "Free" branch — the cents amount only, used
 *  where the caller already knows the price is > 0 (eg the yearly
 *  subline in the plan card). */
export function formatMoneyMinor(amount_minor, currency) {
    const code = currency.toUpperCase();
    const major = amount_minor / 100;
    try {
        const fmt = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: code,
            minimumFractionDigits: Number.isInteger(major) ? 0 : 2,
            maximumFractionDigits: 2,
        });
        return fmt.format(major);
    }
    catch {
        return `${major.toFixed(Number.isInteger(major) ? 0 : 2)} ${code}`;
    }
}
/** Render a limit for display. `null` → "Unlimited"; `retention_days`
 *  becomes a day/month/year phrase; numeric limits use thousand
 *  separators. */
export function formatLimit(limit) {
    if (limit.limit_value === null)
        return 'Unlimited';
    if (limit.dimension === 'retention_days') {
        const d = limit.limit_value;
        if (d >= 365) {
            const years = Math.round(d / 365);
            return years === 1 ? '1 year' : `${years} years`;
        }
        if (d >= 30) {
            const months = Math.round(d / 30);
            return months === 1 ? '1 month' : `${months} months`;
        }
        return d === 1 ? '1 day' : `${d} days`;
    }
    return formatCount(limit.limit_value);
}
/** Format a count — round-millions become "2.5 million", everything
 *  else uses the US locale thousand separator. */
export function formatCount(n) {
    if (n === null)
        return 'Unlimited';
    if (n >= 1e6 && n % 1e5 === 0) {
        const m = n / 1e6;
        return `${m.toLocaleString('en-US', { maximumFractionDigits: 1 })} million`;
    }
    return n.toLocaleString('en-US');
}
/** Pick the first matching price for the interval, falling back to
 *  monthly so a yearly-only or monthly-only plan still renders. */
export function pickPrice(prices, interval) {
    return prices.find((p) => p.interval === interval) ?? prices.find((p) => p.interval === 'monthly');
}
/** Render a whole number ≥ 2 as an English phrase: `2` → "two",
 *  `10` → "ten", `12` → "12". Falls back to the digit form past 10.
 *  Used for the "N times the room" line on a derived pitch.  */
export function phraseForMultiple(n) {
    if (!Number.isInteger(n) || n < 2)
        return '';
    const words = ['', '', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
    return n <= 10 ? words[n] : String(n);
}
/** Resolve the "N times the room" clause for a higher-volume plan,
 *  from the page-loads limit on each. Returns `"N times the room"`
 *  when BOTH limits are finite AND the ratio is a whole number ≥ 2,
 *  else `null` so the caller can drop the clause. The founder can
 *  change the ratio in ops by editing the catalog limits; the pitch
 *  updates on next catalog read.  */
export function pageLoadsRatioClause(basePlan, topPlan) {
    if (basePlan === undefined || topPlan === undefined)
        return null;
    const base = basePlan.limits.find((l) => l.dimension === 'page_loads_monthly');
    const top = topPlan.limits.find((l) => l.dimension === 'page_loads_monthly');
    if (base === undefined || top === undefined)
        return null;
    if (base.limit_value === null || top.limit_value === null)
        return null;
    if (base.limit_value <= 0 || top.limit_value <= 0)
        return null;
    const ratio = top.limit_value / base.limit_value;
    if (!Number.isInteger(ratio) || ratio < 2)
        return null;
    const phrase = phraseForMultiple(ratio);
    return `${phrase} times the room`;
}
/** True iff any plan, add-on or agency plan in the data has a yearly
 *  price. The monthly/yearly toggle hides itself when this is false. */
export function hasYearly(data) {
    for (const plan of data.plans) {
        if (plan.prices.some((p) => p.interval === 'yearly'))
            return true;
    }
    for (const addon of data.addons) {
        if (addon.prices.some((p) => p.interval === 'yearly'))
            return true;
    }
    for (const ag of data.agency_plans ?? []) {
        if (ag.prices.some((p) => p.interval === 'yearly'))
            return true;
    }
    return false;
}
//# sourceMappingURL=format.js.map